import styled, { css } from 'styled-components'
import { font } from '@styles/vars/font.style'
import { colors } from '@styles/vars/colors.style'
import { clamp } from '@styles/utils/conversion.style'
import { cubicBezier } from '@styles/vars/cubicBezier.style'

const outlineMaxFontSize = 18
const textMaxFontSize = 34

export const ButtonMain = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  background-color: transparent;

  a& {
    text-decoration: none;
  }

  ${({ variant, color }) => {
    if (variant === 'outline') {
      if (color === 'charcoal') {
        return `
          color: ${colors.charcoal};
        `
      } else if (color === 'pastelYellow') {
        return `
          color: ${colors.pastelYellow};
        `
      } else {
        return `
          color: ${color};
        `
      }
    }

    if (variant === 'text') {
      return `
        color: ${color};
      `
    }
  }}

  ${({ variant }) => {
    if (variant === 'outline') {
      return ButtonVariantOutline
    }

    if (variant === 'text') {
      return ButtonVariantText
    }
  }}
`

export const ButtonInner = styled.div``

export const ButtonLabel = styled.span`
  position: relative;

  &:before {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    transform-origin: 0% 50%;
    transform: scaleX(0);
    display: block;
    height: 0.1rem;
    background-color: currentColor;
    transition: transform 0.15s ${cubicBezier.out};
  }
`

const ButtonVariantOutline = css`
  font-family: ${font.sans.family};
  font-weight: ${font.sans.weight.regular};
  ${clamp('font-size', 14, outlineMaxFontSize)}
  line-height: 1;
  text-transform: uppercase;
  border: none;
  transition: opacity 0.2s ${cubicBezier.inOut};

  ${ButtonInner} {
    ${({ size }) => {
      if (size === 'large') {
        return `padding: ${12 / outlineMaxFontSize}em ${26 / outlineMaxFontSize}em; 
        `
      } else {
        return `
          padding: ${7.5 / outlineMaxFontSize}em ${16 / outlineMaxFontSize}em;
        `
      }
    }}
    border: 0.15rem solid currentColor;
    border-radius: 7rem;
    transition-property: background-color, color, border-color;
    transition-duration: 0.1s;
    transition-timing-function: ${cubicBezier.inOut};
  }

  ${ButtonInner} {
    ${({ color }) => {
      if (color === 'charcoal') {
        return `
          color: ${colors.white};
          background-color: ${colors.charcoal};
          border-color: ${colors.charcoal};  
        `
      } else if (color === 'white') {
        return `
          color: ${colors.white};
          background-color: ${colors.charcoal};
          border-color: ${colors.charcoal};
        `
      } else if (color === 'pastelYellow') {
        return `
          color: ${colors.red};
          background-color: ${colors.pastelYellow};
          border-color: ${colors.pastelYellow};
        `
      } else if (color === 'filters') {
        return `
          color: ${colors.charcoal};
          background-color: ${colors.white};
          border-color: ${colors.charcoal};
        `
      } else {
        return `
          opacity: 0.5;
        `
      }
    }}
  }

  &[disabled] {
    cursor: default;

    ${ButtonInner} {
      ${({ color }) => {
        if (color === 'charcoal') {
          return `
            color: ${colors.white};
            background-color: ${colors.charcoal};
            border-color: ${colors.charcoal};  
          `
        } else if (color === 'pastelYellow') {
          return `
            color: ${colors.red};
            background-color: ${colors.pastelYellow};
            border-color: ${colors.pastelYellow};
          `
        } else if (color === 'white') {
          return `
            color: ${colors.charcoal};
            background-color: ${colors.white};
            border-color: ${colors.charcoal};
          `
        } else if (color === 'filters') {
          return `
            color: ${colors.white};
            background-color: ${colors.charcoal};
            border-color: ${colors.charcoal};
          `
        } else {
          return `
            opacity: 0.5;
          `
        }
      }}
    }
  }

  &:not(:disabled):hover {
    ${ButtonInner} {
      ${({ color }) => {
        if (color === 'charcoal') {
          return `
            color: ${colors.white};
            background-color: ${colors.charcoal};
            border-color: ${colors.charcoal};
          `
        } else if (color === 'white') {
          return `
            color: ${colors.white};
            background-color: ${colors.charcoal};
            border-color: ${colors.charcoal};
          `
        } else if (color === 'pastelYellow') {
          return `
            color: ${colors.red};
            background-color: ${colors.pastelYellow};
            border-color: ${colors.pastelYellow};
          `
        } else if (color === 'filters') {
          return `
            color: ${colors.white};
            background-color: ${colors.charcoal};
            border-color: ${colors.charcoal};
          `
        } else {
          return `
            color: ${colors.charcoal};
            background-color: ${color};
            border-color: ${color};
          `
        }
      }}
    }
  }
`

const ButtonVariantText = css`
  font-family: ${font.serif.family};
  font-weight: ${font.serif.weight.light};
  ${clamp('font-size', 24, textMaxFontSize)}
  line-height: 1.2;
  letter-spacing: -0.01em;
  background-color: transparent;
  border: none;
  transition-property: color, opacity;
  transition-duration: 0.2s;
  transition-timing-function: ${cubicBezier.inOut};

  &[disabled] {
    opacity: 0.3;
    cursor: default;
  }

  &:not(:disabled):hover {
    ${ButtonLabel}::before {
      transform: scaleX(1);
    }
  }
`

export const ButtonContent = styled.span`
  display: inline-flex;
  align-items: baseline;
  justify-content: center;

  * + * {
    margin-left: ${props =>
      props.variant === 'outline'
        ? '0.5555555556em'
        : props.variant === 'text'
        ? '0.4761904762em'
        : '0.5em'};
  }
`

export const ButtonIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${props =>
    props.variant === 'outline'
      ? '0.6111111111em'
      : props.variant === 'text'
      ? '0.6111111111em'
      : '0.5em'};

  svg {
    width: 100%;
  }
`
